// C1: THE QUOTE WHOSE LINES ARE THE SHARED EXAMPLE SET, AND WHY THEY MUST
// NEVER BE SAVED.
//
// WHAT THIS IS FOR. `api.getMaterials(key)` answers
// `materialsByQuote[key] ?? mockMaterials` -- so any quote nobody has ever
// seeded a list for is handed the ONE shared 180-row example array that
// mocks/materials.ts synthesises. The 25 quotes in mocks/quotes.ts are all
// such quotes: nothing seeds them, so all 25 show the same 180 example lines.
//
// That was harmless while the app was mock-only. It stopped being harmless
// when the draft autosave landed, because the autosave's only content gate is
// `rows.length === 0` -- and 180 is not 0. Opening one of those quotes as a
// user who can edit therefore wrote 180 invented lines into the production
// database 1.5 seconds later, with no button pressed and nothing on screen to
// say so.
//
// MEASURED ON LIVE, 2026-09-07, before this guard existed: 4335 of 4346 quote
// lines in the database carried a part number from this file's own 20-SKU
// example catalog. Eleven lines were real. See the HANDOFF entry.
//
// ⚠️ THE PREDICATE IS THE ABSENT STORE ENTRY, NOT THE CONTENT OF THE ROWS.
//
// The tempting alternative is to look at the lines and decide whether they
// "look like" example data -- match the part numbers, or spot the 180 count.
// That is a heuristic over data, and it fails in both directions: a real
// quote that happens to hold 180 lines would be refused, and an example line
// edited by hand would slip through. The store entry is a FACT about how this
// quote's list came to exist, and it is the same fact the whole materials
// seam already turns on.
//
// ⚠️ NOT A SAFETY CONTROL OVER THE NUMBERS. Compare services/unpricedGuard.ts,
// which stops a wrong PRICE being produced, and services/freightBasisGuard.ts,
// which mirrors an engine refusal. This one stops a wrong RECORD being
// created. The engine is not involved and no pricing input is touched.
//
// ⚠️ C1a: ONE FACT WAS NOT ENOUGH, AND THE ORIGINAL NOTE HERE UNDERSTATED WHY.
//
// This header used to say that a pre-C1 draft restoring from the database
// gives its quote a list of its own, so the first fact reads true and its
// autosave "continues to update that existing row" -- adding "no new row, no
// new line" as though that made it harmless. IT DID NOT.
//
// MEASURED ON LIVE 2026-09-07, after C1 shipped: someone opened two example
// quotes whose pre-C1 drafts restored, and pressed Save. The press was
// ALLOWED. It did not merely update a row -- it FINALISED both, flipping
// is_draft to false, which draws a real number from ir.sequence. Q-2026-0001
// became Q-2026-0154 and Q-2026-0025 became Q-2026-0155, and 360 generated
// lines appeared on /saved-quotes as two numbered final quotes. That is worse
// than the draft leak C1 contained, and the old wording made it sound benign.
//
// SO THERE ARE TWO FACTS NOW, and a quote must satisfy BOTH to be saveable.
// The second one -- IS THIS ONE OF THE 25 EXAMPLE QUOTES WE SHIP? -- is the
// one a restore cannot wash away, because it is a property of our own bundled
// array rather than of any database row.
//
// ⚠️ IT IS STILL NOT A CONTENT HEURISTIC. api.isExampleQuoteNo reads the
// names in mocks/quotes.ts. It never looks at a line, a part number or a row
// count, so the rule this module was built on is intact: no verdict here is
// reached by inspecting data that came out of the database.
//
// ⚠️ AND THE FIRST FACT STAYS. It is honest and it catches what the second
// cannot: a quote reached some other way that was never seeded, and any
// example quote added to mocks/quotes.ts in future before anyone thinks about
// this file. They overlap deliberately.

import type { Material } from "@/types";

/** A quote that is showing the shared example lines rather than its own. */
export interface ExampleDataProblem {
  /** What the user calls this quote. The message says it out loud, because
   *  it is the only name the person reading it has ever seen. */
  quoteRef: string;
  /** How many example lines are on screen. Named so the message can be
   *  specific about the size of what is being declined. */
  lineCount: number;
}

/** The two facts, plus the route. Passed in rather than read here, so this
 *  stays a pure function testable on data.
 *
 *  ⚠️ AN OBJECT, NOT POSITIONAL BOOLEANS. Three flags in a row is an
 *  invitation to transpose two of them, and a transposition here fails in the
 *  silent direction -- it would report every real quote and no example one. */
export interface QuoteFacts {
  /** /saved-quotes/:savedId. A real database record, whatever it is called. */
  isSavedMode: boolean;
  /** api.hasOwnMaterialStore(storeKey) -- did anything deliberately seed this
   *  quote's material list, or is it reading the shared example rows. */
  hasOwnMaterialStore: boolean;
  /** api.isExampleQuoteNo(quoteNo) -- is this reference one of the 25 we ship
   *  in mocks/quotes.ts. A fact about our bundle, never about a row. */
  isExampleQuoteNo: boolean;
}

/** Is this quote one the app must refuse to save?
 *
 *  ⚠️ SAVED MODE RETURNS FIRST, AND THAT IS THE FENCE-1 ANSWER. Live holds TEN
 *  database rows named Q-2026-0001, and after C1a's own fix two more are
 *  called Q-2026-0154 and Q-2026-0155. A name is not identity: the SPA's
 *  shipped example quote and a database row that happens to share its name are
 *  different things, and the ROUTE is what tells them apart.
 *
 *    /quotes/:id             the id IS the reference, and the quote object
 *                            comes from quotesStore. A quote here named
 *                            Q-2026-0001 IS the shipped example one.
 *    /saved-quotes/:savedId  keyed on the BACKEND id. The record is real, its
 *                            name is whatever Odoo stored, and re-saving it is
 *                            a deliberate act on a real quote.
 *
 *  So isExampleQuoteNo is consulted ONLY off the mock route. Putting that rule
 *  here rather than at each call site is deliberate -- there are two seams and
 *  a banner, and a caller that forgot the route check would start refusing
 *  real reopened quotes.
 *
 *  Returns null -- saveable -- in four cases:
 *
 *    saved mode                  a real database record. See above.
 *    a real quote with its list  created here, or forked on a pick.
 *    there are no rows           nothing to save either way, and the
 *                                autosave's own `rows.length === 0` gate
 *                                already returns. A banner on an empty new
 *                                quote would be noise.
 *    no quote reference yet      the saved-quote read is still in flight. The
 *                                loading gates return before the table
 *                                renders; this is belt and braces. */
export function exampleDataProblem(
  quoteRef: string,
  facts: QuoteFacts,
  rows: Material[] | undefined,
): ExampleDataProblem | null {
  if (facts.isSavedMode) return null;
  if (!quoteRef) return null;
  if (!rows || rows.length === 0) return null;
  // EITHER fact condemns it. The shipped-name check is the one a database
  // restore cannot wash away; the store check is the one that catches a quote
  // nobody seeded. Neither subsumes the other -- see the header.
  if (facts.isExampleQuoteNo) return { quoteRef, lineCount: rows.length };
  if (!facts.hasOwnMaterialStore) return { quoteRef, lineCount: rows.length };
  return null;
}

/** The sentence the banner and both save seams say, built once so they cannot
 *  word the same refusal differently. Mirrors unpricedMessage's shape.
 *
 *  ⚠️ THE WORDS "MOCK", "SAMPLE" AND "TEST DATA" ARE FORBIDDEN HERE, and
 *  mockQuoteGuard.test.ts asserts their absence. They are this project's
 *  vocabulary, not a salesperson's: someone reading "mock data" on a quote
 *  screen learns nothing about what to do next, and "test data" actively
 *  suggests something is broken. "Example" is a word that survives being read
 *  by whoever is trying to quote a customer.
 *
 *  ⚠️ IT NAMES A REMEDY THAT EXISTS. New Quote on the quotes list creates a
 *  real quote with an empty material list, which is exactly the thing this
 *  message is telling someone to go and do. If that button ever stops doing
 *  that, this sentence becomes a lie. */
export function exampleDataMessage(problem: ExampleDataProblem | null): string {
  if (!problem) return "";
  const lines = problem.lineCount === 1 ? "line" : "lines";
  return (
    `The ${problem.lineCount} ${lines} shown on ${problem.quoteRef} are ` +
    `example data included with this app, not a real enquiry, so this quote ` +
    `cannot be saved. To quote a customer, create a new quote from the ` +
    `Quotes list and add the materials you need.`
  );
}
